// src/components/views/HomeView.tsx - COMPLETE FIXED
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Product, BrandProfile, UserProfile } from '../../types';
import {
  ArrowRight, Flame, Heart, ChevronRight, Gift, Clock, ArrowLeft, Play, Bell,
  CheckCircle, Zap, TrendingUp, Bomb, Camera, Shell, Dices, ShoppingBag,
  Sparkles, AlertTriangle, Tag, Globe, Gem, Gamepad2, MessageCircle,
  Home, Users, Plus
} from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { db } from '../../services/firebase';
import { doc, onSnapshot, addDoc, collection, query, where, getDocs } from 'firebase/firestore';

// 🟢 IMPORT THE TERMINALS & MODALS (EXISTING)
import { GiveawayTerminal } from '../giveaways/GiveawayTerminal';
import { MysteryDropModal } from '../games/MysteryDropModal';
import { WalletTerminal } from '../wallet/WalletTerminal';

// 🟢 HELPER: Bulletproof Image Fallback (For Products/Drops)
const getValidImage = (item: any) => {
  if (!item) return 'https://images.unsplash.com/photo-1552346154-21d32810baa3?auto=format&fit=crop&q=80&w=800';
  return item.image || item.imageUrl || (item.images && item.images[0]) || item.banner || item.logoUrl || 'https://images.unsplash.com/photo-1552346154-21d32810baa3?auto=format&fit=crop&q=80&w=800';
};

// 🟢 HELPER: Strict Brand Image (Returns null if missing so we can show a black box)
const getBrandImage = (item: any) => {
  if (!item) return null;
  return item.logoUrl || item.image || item.imageUrl || item.banner || null;
};

// 🟢 HELPER: Safe Brand Name Extractor
const getBrandName = (item: any) => {
  if (!item) return '';
  let foundName = item.brand || item.businessName || item.displayName || item.name || '';
  if (foundName === 'Unknown Brand' || foundName === 'Unknown') {
    return '';
  }
  return foundName;
};

// 🟢 REAL-TIME COUNTDOWN TIMER
const CountdownTimer = ({ targetDate }: { targetDate: number }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        if (!targetDate) {
            setTimeLeft('TBA');
            return;
        }

        const updateTimer = () => {
            const now = Date.now();
            const diff = targetDate - now;

            if (diff <= 0) {
                setTimeLeft('DROPPED');
                return;
            }

            const d = Math.floor(diff / (1000 * 60 * 60 * 24));
            const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const m = Math.floor((diff / 1000 / 60) % 60);
            const s = Math.floor((diff / 1000) % 60);

            if (d > 0) {
                setTimeLeft(`${d}d ${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
            } else {
                setTimeLeft(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [targetDate]);

    return <>{timeLeft}</>;
};

// --- MOBILE DROP CARD ---
const DropCard = ({ drop, profile, onEnterGiveaway }: { drop: any, profile: UserProfile | null, onEnterGiveaway: (drop: any) => void }) => {
  return (
    <div onClick={() => onEnterGiveaway(drop)} className="relative min-w-[85%] aspect-[2/1] rounded-[2rem] overflow-hidden group border border-white/10 cursor-pointer snap-center">
      <img src={getValidImage(drop)} className="w-full h-full object-cover grayscale opacity-60" alt={getBrandName(drop)} />
      <div className="absolute top-6 left-6">
        <span className="text-xs font-bold text-white uppercase tracking-widest border border-white/30 px-2 py-0.5 rounded-full backdrop-blur-sm">Giveaway</span>
        <h2 className="text-3xl font-black text-white leading-none mt-2">{getBrandName(drop)}</h2>
      </div>
      <div className="absolute bottom-6 right-6">
         <button onClick={(e) => { e.stopPropagation(); onEnterGiveaway(drop); }} className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm bg-white text-black">
            Enter
         </button>
      </div>
    </div>
  );
};

interface HomeViewProps {
  profile: UserProfile | null;
  products: Product[];
  brands: BrandProfile[];
  wishlist: any[];
  onSelectProduct: (product: Product) => void;
  onToggleWishlist: (productId: string) => void;
  onOpenVirtualTryOn: () => void;
  onOpenCopOrDrop: () => void;
  onViewBrand: (brandId: string) => void;
  updateProfile: (data: any) => Promise<void>;
}

export const HomeView: React.FC<HomeViewProps> = ({
  profile, products, brands, wishlist,
  onSelectProduct, onToggleWishlist, onOpenVirtualTryOn,
  onOpenCopOrDrop, onViewBrand,
  updateProfile
}) => {

  const navigate = useNavigate();

  // 🟢 TOP NAVIGATION TAB STATE
  const [activeTopTab, setActiveTopTab] = useState('brands');

  const [adminAd, setAdminAd] = useState<any>(null);
  const [adminVideo, setAdminVideo] = useState<any>(null);

  // 🏆 BRAND ORDERING — set from the Dashboard
  const [winningBrandId, setWinningBrandId] = useState<string | null>(null);
  const [winningBrandTitle, setWinningBrandTitle] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'platformSettings', 'homeConfig'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAdminAd(data.adminAd || null);
        setAdminVideo(data.adminVideo || null);
        setWinningBrandId(data.winningBrandId || null);
        setWinningBrandTitle(data.winningBrandTitle || null);
      }
    });
    return () => unsub();
  }, []);

  const [selectedGiveaway, setSelectedGiveaway] = useState<any | null>(null);
  const [isMysteryDropOpen, setIsMysteryDropOpen] = useState(false);
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [notifiedDrops, setNotifiedDrops] = useState<string[]>([]);
  const [dropToast, setDropToast] = useState<string | null>(null);
  const [activeMobileCategory, setActiveMobileCategory] = useState<string>('All');

  // 🟢 LOAD SAVED REMINDERS ON MOUNT
  useEffect(() => {
      if (!profile?.uid) return;
      const fetchReminders = async () => {
          try {
              const q = query(collection(db, 'notifications'), where('userId', '==', profile.uid), where('type', '==', 'drop_reminder'));
              const snap = await getDocs(q);
              const remindedIds = snap.docs.map(doc => doc.data().productId);
              setNotifiedDrops(remindedIds);
          } catch (e) { console.error(e); }
      };
      fetchReminders();
  }, [profile?.uid]);

  // 🟢 HARDCODE LOVE.SCOOP & INJECT IT INTO THE FRONT OF THE LIST
  const loveScoopMockBrand: any = {
    id: 'love-scoop',
    businessName: 'Love.Scoop',
    name: 'Love.Scoop',
    description: 'Official platform rewards & digital exclusives.',
    logoUrl: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/love%20scoopp.jpg?alt=media&token=70ac832d-fb42-47e9-934a-b97023f844e5',
    bannerUrl: 'https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/love%20scoopp.jpg?alt=media&token=70ac832d-fb42-47e9-934a-b97023f844e5'
  };

  // 🟢 FILTER OUT THRIFT, DESIGNER, AND JEWELLERY BRANDS
  const authenticBrands = brands.filter(b => {
    const workspaceType = (b as any).workspaceType || 'brand';
    return workspaceType !== 'thrift' && workspaceType !== 'designer' && workspaceType !== 'jewellery';
  });

  // 🟢 SORT BRANDS BY ADMIN RANK (lower = earlier, unranked = last)
  const sortedBrands = [...authenticBrands].sort((a: any, b: any) => {
      const rankA = typeof a.rank === 'number' ? a.rank : 9999;
      const rankB = typeof b.rank === 'number' ? b.rank : 9999;
      return rankA - rankB;
  });

  // 🏆 Pull the winning brand out of the sorted list
  const winningBrand: any = winningBrandId
    ? sortedBrands.find((b: any) => b.id === winningBrandId) || null
    : null;

  const winningBrandTagged: any = winningBrand
    ? { ...winningBrand, isWinningBrand: true, winningBadge: winningBrandTitle || 'Brand of the Week' }
    : null;

  // 🥇 Final order: Winner → Love.Scoop → everything else by rank
  const displayBrands = [
    ...(winningBrandTagged ? [winningBrandTagged] : []),
    loveScoopMockBrand,
    ...sortedBrands.filter((b: any) =>
      b.id !== 'love-scoop' &&
      b.businessName !== 'Love.Scoop' &&
      b.id !== winningBrandId
    )
  ];

  // 🟢 REAL NOTIFICATION LOGIC
  const handleNotifyDrop = async (e: React.MouseEvent, product: any) => {
      e.stopPropagation();

      if (!profile?.uid) {
          setDropToast("Sign in to set a drop reminder!");
          setTimeout(() => setDropToast(null), 3000);
          return;
      }

      if (notifiedDrops.includes(product.id)) return;

      setNotifiedDrops(prev => [...prev, product.id]);
      setDropToast("Reminder set for upcoming drop");
      setTimeout(() => setDropToast(null), 3000);

      try {
        await addDoc(collection(db, 'notifications'), {
            userId: profile.uid,
            title: 'Drop Reminder Set',
            message: `Reminder set for upcoming drop: ${product.name}`,
            type: 'drop_reminder',
            productId: product.id,
            createdAt: new Date().getTime(),
            read: false,
            triggerTime: product.releaseDate
        });
      } catch (err) { console.error("Failed to save notification", err); }
  };

  const isWishlisted = (id: string) => wishlist?.some(w => (typeof w === 'string' ? w === id : w.productId === id));

  // 🟢 FILTER OUT THRIFT, DESIGNER, AND JEWELLERY PRODUCTS
  const brandProducts = products.filter(p => {
    const workspaceType = (p as any).workspaceType || 'brand';
    const productType = (p as any).type || 'brand';

    const isExcluded = workspaceType === 'thrift' ||
                       workspaceType === 'designer' ||
                       workspaceType === 'jewellery' ||
                       productType === 'thrift' ||
                       productType === 'designer' ||
                       productType === 'jewellery';

    return !isExcluded;
  });

  // 🟢 SAFECAT LOGIC
  const safeCat = (p: any) => (p.category || '').toLowerCase();

  const col1_Tops = brandProducts.filter(p => safeCat(p).includes('top') || safeCat(p).includes('shirt'));
  const col2_Bottoms = brandProducts.filter(p => safeCat(p).includes('bottom') || safeCat(p).includes('pant') || safeCat(p).includes('jean'));
  const col3_Outerwear = brandProducts.filter(p => safeCat(p).includes('outerwear') || safeCat(p).includes('hoodie') || safeCat(p).includes('jacket'));
  const col4_Shoes = brandProducts.filter(p => safeCat(p).includes('shoe') || safeCat(p).includes('sneaker'));
  const col5_FullSets = brandProducts.filter(p => safeCat(p).includes('set') || safeCat(p).includes('jumpsuit'));
  const col6_Headwear = brandProducts.filter(p => safeCat(p).includes('headwear') || safeCat(p).includes('hat') || safeCat(p).includes('cap'));
  const col7_Accessories = brandProducts.filter(p => safeCat(p).includes('access') || safeCat(p).includes('bag') || safeCat(p).includes('sock') || safeCat(p).includes('under'));

  // 🟢 SMART CURATION ALGORITHM
  let curatedProducts: Product[] = [];

  const pData: any = profile;
  const userPrefs = pData?.preferences || pData?.styles || pData?.favoriteCategories || [];

  if (userPrefs.length > 0) {
      const matchedProducts = brandProducts.filter(p => {
          const pCategory = (p.category || '').toLowerCase();
          const prodData: any = p;
          const pTags = Array.isArray(prodData.tags) ? prodData.tags.map((t: string) => t.toLowerCase()) : [];

          return userPrefs.some((pref: string) =>
              pCategory.includes(pref.toLowerCase()) || pTags.includes(pref.toLowerCase())
          );
      });

      curatedProducts = matchedProducts;

      if (curatedProducts.length === 0) {
          curatedProducts = brandProducts;
      }
  } else {
      curatedProducts = brandProducts;
  }

  // 🟢 GIVEAWAYS
  const activeGiveaways = brandProducts.filter(p => (p.type as string) === 'giveaway' || p.type === 'raffle');

  // 🟢 UPCOMING DROPS
  const upcomingDrops = brandProducts.filter(p => {
      if (!p.releaseDate) return false;
      const releaseTime = Number(p.releaseDate);
      const oneDayInMs = 24 * 60 * 60 * 1000;
      return releaseTime > (Date.now() - oneDayInMs);
  }).sort((a, b) => Number(a.releaseDate) - Number(b.releaseDate));

  // 🟢 REGULAR PRODUCTS
  const regularProducts = brandProducts.filter(p => (p.type as string) !== 'giveaway' && p.type !== 'raffle');

  const getFilteredMobileProducts = () => {
      if (activeMobileCategory === 'All') return regularProducts;
      if (activeMobileCategory === 'Tops') return regularProducts.filter(p => safeCat(p).includes('top') || safeCat(p).includes('shirt'));
      if (activeMobileCategory === 'Bottoms') return regularProducts.filter(p => safeCat(p).includes('bottom') || safeCat(p).includes('pant'));
      if (activeMobileCategory === 'Outerwear') return regularProducts.filter(p => safeCat(p).includes('outerwear') || safeCat(p).includes('hoodie') || safeCat(p).includes('jacket'));
      if (activeMobileCategory === 'Shoes') return regularProducts.filter(p => safeCat(p).includes('shoe') || safeCat(p).includes('sneaker'));
      if (activeMobileCategory === 'Full Sets') return regularProducts.filter(p => safeCat(p).includes('set'));
      if (activeMobileCategory === 'Headwear') return regularProducts.filter(p => safeCat(p).includes('headwear') || safeCat(p).includes('hat'));
      if (activeMobileCategory === 'Accessories') return regularProducts.filter(p => safeCat(p).includes('access'));
      if (activeMobileCategory === 'Bags') return regularProducts.filter(p => safeCat(p).includes('bag'));
      if (activeMobileCategory === 'Socks') return regularProducts.filter(p => safeCat(p).includes('sock'));
      if (activeMobileCategory === 'Underwear') return regularProducts.filter(p => safeCat(p).includes('under'));
      return regularProducts;
  };

  const mobileCategories = ['All', 'Tops', 'Bottoms', 'Outerwear', 'Shoes', 'Full Sets', 'Accessories', 'Headwear', 'Bags', 'Socks', 'Underwear'];

  const customStyles = `
    @keyframes marquee {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .animate-marquee {
      display: inline-block;
      white-space: nowrap;
      animation: marquee 15s linear infinite;
    }
  `;

  return (
    <div className="flex-1 bg-black min-h-screen pb-20 w-full relative">
      <style>{customStyles}</style>

      <MysteryDropModal
        isOpen={isMysteryDropOpen}
        onClose={() => setIsMysteryDropOpen(false)}
        onOpenBuyCredits={() => {
            setIsMysteryDropOpen(false);
            setIsWalletOpen(true);
        }}
      />

      <WalletTerminal
          isOpen={isWalletOpen}
          onClose={() => setIsWalletOpen(false)}
          currentUser={profile}
      />

      {/* =========================================================================
          📱 MOBILE LAYOUT (< 1024px)
      ========================================================================= */}
      <div className="lg:hidden flex flex-col min-h-screen">

        <div className="flex-1 overflow-y-auto pb-24">

          {/* BRANDS TAB CONTENT */}
          {activeTopTab === 'brands' && (
            <>
              {/* HEADER */}
              <div className="px-6 pt-6 animate-in fade-in slide-in-from-right duration-500">
                <h2 className="text-2xl font-black text-white mb-2 italic tracking-tighter">SCOOP<span className="text-white/50">.</span></h2>
                <p className="text-white/50 text-xs font-bold uppercase tracking-widest">Welcome back, {profile?.displayName ? profile.displayName.split(' ')[0] : 'Guest'}</p>
              </div>

              {/* MOBILE AD SPACE */}
              {adminAd && (adminAd.imageUrl || adminAd.videoUrl) && (
                <div className="px-6 mt-4">
                    <a href={adminAd.link || '#'} target="_blank" rel="noopener noreferrer" className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden block border border-white/20 cursor-pointer group shrink-0">
                      {adminAd.videoUrl ? (
                        <video src={adminAd.videoUrl} autoPlay muted loop playsInline className="w-full h-full object-cover grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
                      ) : (
                        <img src={adminAd.imageUrl} className="w-full h-full object-cover grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" alt="Advertisement" />
                      )}
                      <div className="absolute top-4 left-4 bg-white text-black text-[9px] font-black uppercase tracking-widest px-2 py-1 shadow-lg">Advertisement</div>
                      <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border border-white/20 flex items-center gap-2">Explore <ArrowRight size={12} /></div>
                    </a>
                </div>
              )}

              {/* 🏆 WINNING BRAND — HERO CARD */}
              {winningBrandTagged && (
                <div className="px-6 mt-6">
                  <button
                    onClick={() => onViewBrand(winningBrandTagged.id)}
                    className="relative w-full aspect-[16/7] rounded-2xl overflow-hidden border-2 border-yellow-400 shadow-[0_0_30px_rgba(250,204,21,0.25)] group"
                  >
                    {getBrandImage(winningBrandTagged) ? (
                      <img
                        src={getBrandImage(winningBrandTagged)!}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                        alt={getBrandName(winningBrandTagged)}
                      />
                    ) : (
                      <div className="w-full h-full bg-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 bg-yellow-400 text-black text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                      🏆 {winningBrandTagged.winningBadge}
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-left">
                      <h3 className="text-2xl font-black text-white italic tracking-tighter leading-none">
                        {getBrandName(winningBrandTagged)}
                      </h3>
                      <p className="text-white/60 text-[10px] uppercase tracking-widest font-bold mt-1">
                        Tap to explore the winning collection
                      </p>
                    </div>
                  </button>
                </div>
              )}

              {/* CURATED FOR YOU */}
              {curatedProducts.length > 0 && (
                  <div className="px-6 mt-6">
                      <h3 className="text-xs font-bold text-white/50 tracking-widest mb-4">FOR YOU</h3>
                      <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x pb-2">
                          {curatedProducts.map(product => (
                              <div key={product.id} onClick={() => onSelectProduct(product)} className="shrink-0 w-48 snap-start cursor-pointer group">
                                  <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-zinc-900 border border-white/10 mb-3 relative">
                                      <img src={getValidImage(product)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={product.name} />
                                      <div className="absolute top-2 left-2 bg-white text-black px-2 py-1 rounded-md text-[10px] font-black font-mono">
                                          {formatCurrency(product.price)}
                                      </div>
                                  </div>
                                  <div className="px-1">
                                      <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider mb-0.5">{getBrandName(product)}</p>
                                      <h4 className="text-white font-bold text-xs truncate">{product.name}</h4>
                                  </div>
                              </div>
                          ))}
                      </div>
                  </div>
              )}

              {/* BRANDS */}
              <div className="px-6 mt-6">
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xs font-bold text-white/50 tracking-widest">BRANDS</h3>
                      <a
                          href="https://brands.scoophype.co.za"
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10px] font-black uppercase tracking-widest bg-white text-black px-4 py-2 rounded-full hover:scale-105 active:scale-95 transition-transform"
                      >
                          Start Own Brand
                      </a>
                  </div>

                  <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
                      {displayBrands.map((brand) => {
                          const isLoveScoop = brand.id === 'love-scoop';
                          const isWinner = (brand as any).isWinningBrand;

                          return (
                          <button key={brand.id} onClick={() => onViewBrand(brand.id)} className="w-32 flex-shrink-0 group text-center">
                              <div className={`w-32 h-20 rounded-2xl overflow-hidden relative border transition-all mb-2 ${
                                isWinner
                                  ? 'border-yellow-400 shadow-[0_0_20px_rgba(250,204,21,0.35)]'
                                  : isLoveScoop
                                    ? 'border-white shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                                    : 'border-white/10 group-hover:border-white/50'
                              }`}>
                                  {getBrandImage(brand) ? (
                                      <img src={getBrandImage(brand)} className={`w-full h-full object-cover transition-all ${isLoveScoop || isWinner ? 'opacity-80 group-hover:opacity-100' : 'grayscale group-hover:grayscale-0'}`} alt={getBrandName(brand)} />
                                  ) : (
                                      <div className="w-full h-full bg-black flex items-center justify-center">
                                          <span className="text-zinc-800 font-black text-2xl uppercase opacity-50">{getBrandName(brand).charAt(0)}</span>
                                      </div>
                                  )}
                                  <div className={`absolute inset-0 flex items-center justify-center ${isLoveScoop || isWinner ? 'bg-black/60 group-hover:bg-black/40' : 'bg-black/40 group-hover:bg-black/20'}`}>
                                      <span className={`font-black uppercase text-xs tracking-wider text-white`}>
                                          {isWinner && <span className="mr-1">🏆</span>}
                                          {!isWinner && isLoveScoop && <Sparkles size={10} className="inline mr-1 -mt-0.5" />}
                                          {getBrandName(brand)}
                                      </span>
                                  </div>
                              </div>
                          </button>
                      )})}
                  </div>
              </div>

              {/* MOBILE CATEGORY PILLS & FEED */}
              <div className="pt-4">
                  <div className="flex gap-3 overflow-x-auto scrollbar-hide px-6 pb-6">
                      {mobileCategories.map(cat => (
                          <button
                              key={cat}
                              onClick={() => setActiveMobileCategory(cat)}
                              className={`shrink-0 px-5 py-2 rounded-full border text-[10px] font-black uppercase tracking-widest transition-colors ${activeMobileCategory === cat ? 'bg-white text-black border-white' : 'bg-zinc-950 text-zinc-500 border-zinc-800 hover:border-zinc-600'}`}
                          >
                              {cat}
                          </button>
                      ))}
                  </div>

                  <div className="px-6 grid grid-cols-2 gap-4 pb-8">
                      {getFilteredMobileProducts().length === 0 ? (
                          <div className="col-span-2 text-center py-12 bg-zinc-950 rounded-2xl border border-dashed border-zinc-800">
                              <p className="text-zinc-600 font-bold text-xs uppercase tracking-widest">No Items Found</p>
                          </div>
                      ) : (
                          getFilteredMobileProducts().map(product => (
                              <div key={product.id} onClick={() => onSelectProduct(product)} className="group cursor-pointer">
                                  <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-zinc-900 border border-white/10 mb-3 relative">
                                      <img src={getValidImage(product)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={product.name} />
                                      <button onClick={(e) => { e.stopPropagation(); onToggleWishlist(product.id); }} className="absolute top-2 right-2 p-2 bg-black/30 backdrop-blur-md rounded-full text-white"><Heart size={14} fill={isWishlisted(product.id) ? "white" : "none"} /></button>
                                  </div>
                                  <div className="px-1">
                                      <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider mb-0.5">{getBrandName(product)}</p>
                                      <h4 className="text-white font-bold text-sm truncate mb-1">{product.name}</h4>
                                      <p className="text-white font-medium text-xs">{formatCurrency(product.price)}</p>
                                  </div>
                              </div>
                          ))
                      )}
                  </div>
              </div>
            </>
          )}

          {/* THRIFT TAB - Navigates to /thrifts */}
          {activeTopTab === 'thrift' && (
            <div className="px-6 py-8 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 animate-spin rounded-full border-4 border-white/20 border-t-white"></div>
                <p className="text-zinc-500 text-xs">Loading Thrift Closet...</p>
              </div>
            </div>
          )}

          {/* DESIGNER TAB - Navigates to /designer */}
          {activeTopTab === 'designer' && (
            <div className="px-6 py-8 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 animate-spin rounded-full border-4 border-white/20 border-t-white"></div>
                <p className="text-zinc-500 text-xs">Loading Designer Gate...</p>
              </div>
            </div>
          )}

          {/* JEWELLERY TAB - Navigates to /jewellery */}
          {activeTopTab === 'jewellery' && (
            <div className="px-6 py-8 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 animate-spin rounded-full border-4 border-white/20 border-t-white"></div>
                <p className="text-zinc-500 text-xs">Loading Jewellery Studio...</p>
              </div>
            </div>
          )}

        </div>

        {/* 🟢 MOBILE MARKETPLACE BUTTONS - Show on brands tab only */}
        {activeTopTab === 'brands' && (
          <div className="px-6 pb-6">
            <div className="flex gap-4">
              <div
                onClick={() => navigate('/thrifts')}
                className="relative flex-1 h-28 rounded-2xl bg-white border border-zinc-200 overflow-hidden flex flex-col items-center justify-center p-4 cursor-pointer group active:scale-95 transition-all shadow-lg"
              >
                <div className="relative z-10 flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center shadow-lg mb-2 group-hover:bg-zinc-800 transition-colors">
                    <Tag size={18} className="text-white" />
                  </div>
                  <h3 className="text-sm font-black text-black italic leading-tight">THRIFT CLOSET</h3>
                  <p className="text-[8px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">Marketplace</p>
                </div>
              </div>

              <div
                onClick={() => navigate('/designer')}
                className="relative flex-1 h-28 rounded-2xl bg-black border border-white/20 overflow-hidden flex flex-col items-center justify-center p-4 cursor-pointer group active:scale-95 transition-all shadow-lg hover:border-white/50"
              >
                <div className="relative z-10 flex flex-col items-center text-center">
                  <ShoppingBag size={20} className="text-white/70 group-hover:text-white transition-colors mb-2" />
                  <h3 className="text-sm font-black text-white italic leading-tight">DESIGNER GATE</h3>
                  <p className="text-[8px] uppercase tracking-widest text-white/50 font-bold mt-0.5">Manufactures</p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* =========================================================================
          🖥️ DESKTOP LAYOUT (>= 1024px)
      ========================================================================= */}
      <div className="hidden lg:block w-full">

        <div className="w-full px-8 py-4 flex justify-between items-center border-b border-zinc-800">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">
            Create, Collaborate, Dominate
          </p>
        </div>

        {/* 1. HERO SECTION */}
        <div className="flex flex-col lg:flex-row gap-0 border-b border-zinc-800 h-[600px] w-full bg-black">
          <div className="w-full lg:w-[30%] h-full bg-zinc-950 border-r border-zinc-800 flex flex-col relative overflow-hidden group shrink-0">
            {adminAd && (adminAd.imageUrl || adminAd.videoUrl) ? (
              <a href={adminAd.link || '#'} target="_blank" rel="noopener noreferrer" className="w-full h-full block relative cursor-pointer">
                {adminAd.videoUrl ? (
                  <video src={adminAd.videoUrl} autoPlay muted loop playsInline className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                ) : (
                  <img src={adminAd.imageUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" alt="Advertisement" />
                )}
                <div className="absolute top-8 left-8 bg-white text-black text-[9px] font-black uppercase tracking-widest px-3 py-1.5 border border-black shadow-xl">
                  Advertisement
                </div>
              </a>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-12 text-center relative overflow-hidden border-none">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, #fff 2px, #fff 4px)' }}></div>
                <Flame size={48} className="text-zinc-800 mb-8 z-10" />
                <h2 className="text-7xl font-black italic tracking-tighter text-white glitch-text z-10 leading-none">
                  SCOOP<br/>HYPE
                </h2>
                <div className="absolute top-8 left-8 bg-white text-black text-[9px] font-black uppercase tracking-widest px-3 py-1.5 z-10 shadow-xl">
                  Signal Lost
                </div>
              </div>
            )}
          </div>

          <div className="w-full lg:w-[70%] h-full flex flex-col pt-12 pb-12 bg-black">
            <div className="flex items-end justify-between mb-10 px-8">
              <h2 className="text-5xl font-black text-white uppercase italic tracking-tighter">
                Curated For You
              </h2>
              <div className="flex gap-2">
                <button className="p-4 bg-zinc-950 border border-zinc-800 hover:bg-white hover:text-black transition-colors rounded-none"><ArrowLeft size={20} className="rotate-180" /></button>
              </div>
            </div>

            <div className="flex-1 flex gap-6 overflow-x-auto snap-x scrollbar-hide px-8 pb-4">
              {curatedProducts.map(product => (
                <div key={product.id} onClick={() => onSelectProduct(product)} className="w-72 shrink-0 snap-start bg-zinc-950 border border-zinc-800 rounded-none overflow-hidden cursor-pointer group flex flex-col">
                  <div className="flex-1 bg-black relative overflow-hidden border-b border-zinc-800">
                    <img src={getValidImage(product)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" alt={product.name}/>
                    <div className="absolute top-4 right-4 bg-white text-black px-3 py-1.5 text-xs font-black font-mono border border-black shadow-xl">
                      {formatCurrency(product.price)}
                    </div>
                  </div>
                  <div className="p-6 bg-zinc-950 h-28 flex flex-col justify-center">
                    <h3 className="text-white font-black uppercase text-base truncate tracking-tight">{product.name}</h3>
                    <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest mt-1">{getBrandName(product)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 2. GIVEAWAYS */}
        {activeGiveaways.length > 0 && (
          <div className="w-full border-b border-zinc-800 py-20 bg-black">
            <div className="flex items-center justify-between mb-10 px-8">
              <div className="flex items-center gap-4">
                <Gift size={32} className="text-white" />
                <h2 className="text-5xl font-black text-white uppercase italic tracking-tighter">Giveaways</h2>
              </div>
              <p className="hidden md:block text-zinc-500 font-mono text-[10px] uppercase tracking-[0.3em]">
                Verified Brand Owners Only • Terms Apply
              </p>
            </div>

            <div className="flex gap-8 overflow-x-auto snap-x scrollbar-hide px-8 pb-4">
              {activeGiveaways.map((giveaway: any, i) => (
                <div key={giveaway.id || i} className="w-96 shrink-0 snap-start bg-black border border-zinc-800 rounded-none overflow-hidden group relative h-[420px] flex flex-col">
                  <div className="relative flex-1 bg-zinc-900 overflow-hidden border-b border-zinc-800 cursor-pointer" onClick={() => setSelectedGiveaway(giveaway)}>
                    <img src={getValidImage(giveaway)} className="w-full h-full object-cover opacity-40 group-hover:opacity-70 transition-all duration-700 grayscale group-hover:grayscale-0" alt="Giveaway"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                    <div className="absolute top-6 left-6 flex flex-col gap-2">
                      <div className="bg-white text-black text-[9px] font-black uppercase tracking-[0.2em] px-3 py-1 w-fit border border-black shadow-xl">Live Entry</div>
                    </div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="text-3xl font-black text-white uppercase tracking-tighter leading-tight mb-1">{getBrandName(giveaway)} Special</h3>
                      <p className="text-zinc-400 text-[10px] font-mono uppercase tracking-widest">{giveaway.name}</p>
                    </div>
                  </div>
                  <div className="p-6 bg-zinc-950 flex flex-col gap-4">
                    <div className="flex items-start gap-2">
                      <div className="mt-1 p-1 bg-zinc-800"><Clock size={10} className="text-zinc-400" /></div>
                      <p className="text-[10px] text-zinc-500 font-mono leading-relaxed uppercase">Brand Rules: Must follow brand, like post, and tag 2 creators to be eligible.</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setSelectedGiveaway(giveaway); }} className="w-full py-4 bg-white text-black font-black uppercase tracking-[0.2em] text-[11px] hover:bg-zinc-200 transition-colors shadow-2xl flex items-center justify-center gap-2 group/btn">
                      Enter Giveaway <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3. UPCOMING DROPS */}
        {upcomingDrops.length > 0 && (
          <div className="w-full py-20 bg-black relative">
            {dropToast && (
              <div className="fixed top-24 inset-x-0 flex justify-center z-[300] pointer-events-none">
                <div className="bg-white text-black px-8 py-5 rounded-none flex items-center gap-3 animate-in slide-in-from-top-8 fade-in duration-300 w-max pointer-events-auto">
                  <AlertTriangle size={20} strokeWidth={2.5} className="text-black" />
                  <span className="font-black uppercase tracking-widest text-xs">{dropToast}</span>
                </div>
              </div>
            )}
            <div className="flex items-center gap-4 mb-10 px-8">
              <Clock size={32} className="text-white" />
              <h2 className="text-5xl font-black text-white uppercase italic tracking-tighter">Upcoming Drops</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 w-full border-t border-b border-zinc-800">
              {upcomingDrops.map((product, i) => {
                const isNotified = notifiedDrops.includes(product.id);
                const hasDropped = Number(product.releaseDate) <= Date.now();
                const isPreorder = (product as any).isPreorder;

                return (
                <div
                  key={product.id}
                  className={`bg-zinc-950 border-b md:border-b-0 md:border-r border-zinc-800 ${i === upcomingDrops.length - 1 ? 'md:border-r-0' : ''} rounded-none overflow-hidden group relative transition-all duration-500`}
                >
                  <div
                    className="aspect-[4/5] bg-black relative border-b border-zinc-800 overflow-hidden cursor-pointer"
                    onClick={(e) => {
                      if (hasDropped) {
                        onSelectProduct(product);
                      } else {
                        setDropToast("This product isn't available yet. Set an alarm!");
                        setTimeout(() => setDropToast(null), 3000);
                      }
                    }}
                  >
                    <img src={getValidImage(product)} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale opacity-60 group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" alt={product.name}/>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[2px] group-hover:backdrop-blur-none transition-all duration-500 pointer-events-none text-center px-4">
                      <p className="text-white font-black text-4xl italic tracking-tighter mb-1">
                        {hasDropped ? (isPreorder ? 'PRE-ORDER LIVE' : 'DROPPED') : <CountdownTimer targetDate={Number(product.releaseDate)} />}
                      </p>
                      {!hasDropped && <p className="text-zinc-400 text-[10px] font-mono uppercase tracking-[0.3em]">Until Launch</p>}
                    </div>
                    <div className="absolute bottom-0 left-0 w-full p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out z-20">
                      {hasDropped ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); onSelectProduct(product); }}
                          className="w-full py-4 font-black uppercase text-[10px] tracking-[0.2em] flex items-center justify-center gap-2 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-colors bg-white text-black hover:bg-zinc-200"
                        >
                          {isPreorder ? <Clock size={14} className="text-black" /> : <ShoppingBag size={14} fill="black" />}
                          {isPreorder ? 'Secure Pre-Order' : 'Go to Product'}
                        </button>
                      ) : (
                        <button
                          onClick={(e) => handleNotifyDrop(e, product)}
                          disabled={isNotified || !profile?.uid}
                          className={`w-full py-4 font-black uppercase text-[10px] tracking-[0.2em] flex items-center justify-center gap-2 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-colors ${
                            !profile?.uid
                              ? 'bg-zinc-900 text-zinc-500 cursor-not-allowed'
                              : isNotified
                                ? 'bg-green-500 text-black border border-green-500'
                                : 'bg-white text-black hover:bg-zinc-200'
                          }`}
                        >
                          {!profile?.uid ? (
                            <><Bell size={14} /> Login to Notify</>
                          ) : isNotified ? (
                            <><CheckCircle size={16} strokeWidth={2.5} /> Reminder Set</>
                          ) : (
                            <><Bell size={14} fill="black" /> Notify Me</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="p-6 bg-zinc-950 flex flex-col gap-1">
                    <div className="flex justify-between items-start">
                      <h3 className="text-white font-black uppercase text-base tracking-tight truncate w-[70%]">{product.name}</h3>
                      <span className="text-zinc-600 font-mono text-[10px] uppercase">[ DROP_0{i+1} ]</span>
                    </div>
                    <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest">{getBrandName(product)}</p>
                  </div>
                </div>
              )})}
            </div>
          </div>
        )}

        {/* 4. EDITORIAL VIDEO */}
        <div className="w-full border-y border-zinc-800 relative overflow-hidden group cursor-pointer bg-black">
          <div className="w-full aspect-[21/9]">
            {adminVideo?.videoUrl ? (
              <a href={adminVideo.link || '#'} className="w-full h-full block relative">
                <video src={adminVideo.videoUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" autoPlay muted loop playsInline />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/10 transition-all duration-700"></div>
                <div className="absolute bottom-12 left-12 z-10">
                  <h3 className="text-6xl font-black text-white uppercase italic tracking-tighter drop-shadow-lg leading-none">{adminVideo.title || "LATEST CAMPAIGN"}</h3>
                  <p className="text-zinc-300 text-sm font-mono uppercase tracking-[0.2em] mt-4 drop-shadow-md">{adminVideo.subtitle}</p>
                </div>
                <div className="absolute top-8 right-8 bg-white text-black text-[10px] font-black uppercase tracking-[0.2em] px-4 py-2 shadow-xl flex items-center gap-2 group-hover:scale-105 transition-transform">
                  <Play size={14} fill="currentColor" /> Watch Now
                </div>
              </a>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center bg-black relative">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, #fff 2px, #fff 4px)' }}></div>
                <div className="w-20 h-20 border border-zinc-800 bg-zinc-950 flex items-center justify-center rounded-none mb-8 z-10">
                  <Play size={32} className="text-zinc-700" fill="currentColor" />
                </div>
                <h3 className="text-4xl font-black text-white uppercase tracking-tighter italic z-10">Awaiting Signal</h3>
                <p className="text-zinc-600 font-mono text-xs uppercase tracking-[0.3em] mt-3 z-10">Editorial Campaign Space</p>
              </div>
            )}
          </div>
        </div>

        {/* 🟢 5. CURATED BRANDS (DESKTOP) */}
        <div className="w-full py-20 border-b border-zinc-800 px-8 bg-black">

          <div className="flex justify-between items-end mb-10 pb-6 border-b border-zinc-800 w-full">
            <h2 className="text-5xl font-black text-white uppercase italic tracking-tighter">
              Curated Brands
            </h2>
            <a
              href="https://brands.scoophype.co.za"
              target="_blank"
              rel="noreferrer"
              className="text-xs font-black uppercase tracking-widest bg-white text-black px-6 py-3 rounded-none hover:bg-zinc-200 transition-colors"
            >
              Start Own Brand
            </a>
          </div>

          <div className="flex gap-8 overflow-x-auto snap-x scrollbar-hide w-full pb-8">
            {displayBrands.map(brand => {
              const isLoveScoop = brand.id === 'love-scoop';
              const isWinner = (brand as any).isWinningBrand;

              return (
              <button key={brand.id} onClick={() => onViewBrand(brand.id)} className="shrink-0 w-48 xl:w-56 snap-start flex flex-col items-center gap-4 group">
                <div className={`w-full aspect-square bg-zinc-950 border rounded-none flex items-center justify-center overflow-hidden transition-all relative ${
                  isWinner
                    ? 'border-yellow-400 shadow-[0_0_25px_rgba(250,204,21,0.35)]'
                    : isLoveScoop
                      ? 'border-white shadow-[0_0_20px_rgba(245,158,11,0.2)]'
                      : 'border-zinc-800 group-hover:border-white'
                }`}>
                  {getBrandImage(brand) ? (
                    <img src={getBrandImage(brand)} className={`w-full h-full object-cover transition-all duration-500 scale-95 group-hover:scale-100 ${isLoveScoop || isWinner ? 'opacity-90 group-hover:opacity-100' : 'grayscale group-hover:grayscale-0'}`} alt={getBrandName(brand)} />
                  ) : (
                    <div className="w-full h-full bg-black flex items-center justify-center">
                      <span className="text-zinc-800 font-black text-6xl uppercase opacity-50">{getBrandName(brand).charAt(0)}</span>
                    </div>
                  )}
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isLoveScoop || isWinner ? 'bg-black/60 group-hover:bg-black/40' : 'bg-black/80 opacity-0 group-hover:opacity-100'}`}>
                    <span className={`text-[10px] font-black uppercase tracking-widest text-white`}>
                      {isWinner ? '🏆 Winner' : isLoveScoop ? 'Credit Shop' : 'View'}
                    </span>
                  </div>
                </div>
                <span className={`text-xs font-black uppercase tracking-widest transition-colors text-center w-full truncate ${isWinner ? 'text-yellow-400' : isLoveScoop ? 'text-white' : 'text-zinc-500 group-hover:text-white'}`}>
                  {isWinner && '🏆 '}
                  {getBrandName(brand)}
                </span>
                {isWinner && (
                  <span className="-mt-3 bg-yellow-400 text-black text-[8px] font-black uppercase tracking-widest px-2 py-0.5">
                    {(brand as any).winningBadge || 'Winner'}
                  </span>
                )}
              </button>
            )})}
          </div>
        </div>

        {/* 🟢 6. CATEGORY COLUMNS */}
        <section className="w-full bg-black min-h-screen flex flex-col">
          <div className="px-8 py-10 border-b border-zinc-800 sticky top-0 z-10 bg-black/80 backdrop-blur-xl">
            <h3 className="text-5xl font-black uppercase italic tracking-tighter text-white">Curated Categories</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 border-b border-zinc-800 w-full flex-1">

            {[
              { title: "Tops", data: col1_Tops },
              { title: "Bottoms", data: col2_Bottoms },
              { title: "Outerwear", data: col3_Outerwear },
              { title: "Shoes", data: col4_Shoes },
              { title: "Full Sets", data: col5_FullSets },
              { title: "Headwear", data: col6_Headwear },
              { title: "Accessories", data: col7_Accessories },
            ].map((column, colIndex) => {
              return (
                <div key={colIndex} className="border-r border-zinc-800 last:border-r-0 flex flex-col h-full bg-black">
                  <div className="p-5 text-center border-b border-zinc-800 bg-zinc-950 sticky top-[120px] z-10">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">{column.title}</span>
                  </div>

                  {column.data.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 min-h-[300px] border-b border-zinc-800 bg-zinc-950/30">
                      <div className="w-12 h-12 border border-zinc-800 flex items-center justify-center rotate-45 mb-4">
                        <div className="w-1.5 h-1.5 bg-zinc-700 rounded-full"></div>
                      </div>
                      <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-600 text-center">Coming Soon</span>
                    </div>
                  ) : (
                    <div className="flex flex-col flex-1">
                      {column.data.map((p, i) => {
                        const pAny = p as any;
                        const isOnSale = pAny.onSale || pAny.isOnSale || (pAny.salePrice && pAny.salePrice < p.price);
                        const displayPrice = isOnSale ? pAny.salePrice : p.price;

                        return (
                          <div
                            key={p.id}
                            onClick={() => onSelectProduct(p)}
                            className="relative group aspect-square border-b border-zinc-800 cursor-pointer overflow-hidden bg-black flex flex-col shrink-0"
                          >
                            <div className="absolute top-4 left-4 z-20 flex flex-col gap-2">
                              <span className="font-mono text-zinc-500 text-xs">{(i + 1).toString().padStart(2, '0')}</span>
                              {isOnSale && (
                                <span className="bg-white text-black text-[9px] font-black uppercase tracking-widest px-2 py-0.5">Sale</span>
                              )}
                            </div>

                            <div className="flex-1 relative overflow-hidden">
                              <img
                                src={getValidImage(p)}
                                className="absolute inset-0 w-full h-full object-cover grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 transform group-hover:scale-105"
                                alt={p.name}
                              />
                            </div>

                            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black via-black/80 to-transparent translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                              <div className="flex justify-between items-end opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="flex-1 min-w-0 pr-2">
                                  <h4 className="text-[10px] font-black text-white uppercase tracking-widest truncate">{getBrandName(p)}</h4>
                                  <p className="text-[9px] font-mono text-zinc-400 truncate mt-0.5">{p.name}</p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-[10px] font-mono text-white tracking-widest">{formatCurrency(displayPrice)}</p>
                                  {isOnSale && (
                                    <p className="text-[9px] font-mono text-zinc-500 line-through">{formatCurrency(p.price)}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-auto border-b border-zinc-800 group/btn cursor-pointer bg-zinc-950 hover:bg-white transition-colors duration-300 p-6 flex justify-center items-center shrink-0">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 group-hover/btn:text-black transition-colors duration-300">
                      View All {column.title}
                    </span>
                  </div>

                </div>
              );
            })}
          </div>
        </section>

        {/* 🟢 7. COLLECTIONS & COMMUNITY - Desktop Footer Navigation */}
        <div className="w-full py-12 border-b border-zinc-800 px-8 bg-black">
          <div className="flex justify-center gap-16">
            <button
              onClick={() => navigate('/collections')}
              className="text-sm font-black uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
            >
              Collections
            </button>
            <button
              onClick={() => navigate('/community')}
              className="text-sm font-black uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
            >
              Community
            </button>
          </div>
        </div>

      </div>

      {/* 🟢 THE TERMINAL MOUNT */}
      <GiveawayTerminal
        isOpen={!!selectedGiveaway}
        onClose={() => setSelectedGiveaway(null)}
        giveaway={selectedGiveaway}
        currentUser={profile}
      />

    </div>
  );
};